import type {
  HarmonyPackageBoard,
  HarmonyPackageBoardColumn,
  HarmonyPackageBoardWorkitem,
  HarmonyWorkitem,
  Workitem,
} from '@rhythmic/db/types'
import type { KanbanCard } from '@rhythmic/types'
import type { Collection, UseLiveQueryReturn } from '@tanstack/vue-db'
import { createHooks } from 'hookable'
import type { Ref } from 'vue'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'
import useCollectionRegistry from '../../collections/CollectionRegistry'
import type { HarmonyItem } from '../../queries'
import { useBoardById, useBoardWorkitemsByBoardId, useColumnsByBoardId, useHarmonyItems } from '../../queries'
import {
  getBoardItemsCollection,
  getBoardsCollection,
  getColumnsCollection,
  getHarmonyItemsCollection,
  getWorkitemsCollection,
} from '../getCollections'
import useKanbanBoardContextOnBoardIdChanged from './events/useKanbanBoardContext.onBoardIdChanged'
import useKanbanBoardContextOnContextChanged from './events/useKanbanBoardContext.onContextChanged'
import useKanbanBoardContextOnPlanIdChanged from './events/useKanbanBoardContext.onPlanIdChanged'
import type { KanbanBoardContextEvents } from './useKanbanBoardContext.events.schema'

interface KanbanBoardContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
  error: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface InitKanbanBoardContextQueriesParams {
  logger: KanbanBoardContextLoggerLike
  teamId: Readonly<Ref<string | undefined>>
  workspaceId: Readonly<Ref<string | undefined>>
}

/**
 * Kanban board context query orchestrator with explicit, hook-driven updates.
 *
 * Route params are synced into `harmonyPlanId` / `boardId` by
 * `kanban-board-context-router.client` and then propagated via `hookable` events.
 *
 * Resolved ids (workspace/team) are observed and re-emitted as a `ContextChanged` event.
 * The `ContextChanged` handler owns setting/clearing all collections + live queries.
 */
export default function useKanbanBoardContextQueries() {
  type KanbanBoardContextHookMap = {
    [K in keyof KanbanBoardContextEvents]: (event: KanbanBoardContextEvents[K]) => void | Promise<void>
  }

  const hooks = markRaw(createHooks<KanbanBoardContextHookMap>())

  const harmonyPlanId = shallowRef<string | undefined>(undefined)
  const boardId = shallowRef<string | undefined>(undefined)

  const boardsCollection = ref<Collection<HarmonyPackageBoard> | undefined>(undefined)
  const columnsCollection = ref<Collection<HarmonyPackageBoardColumn> | undefined>(undefined)
  const boardItemsCollection = ref<Collection<HarmonyPackageBoardWorkitem> | undefined>(undefined)
  const packageItemsCollection = ref<Collection<HarmonyWorkitem> | undefined>(undefined)
  const workitemsCollection = ref<Collection<Workitem> | undefined>(undefined)
  const boardQuery = shallowRef<UseLiveQueryReturn<HarmonyPackageBoard> | undefined>(undefined)
  const columnsQuery = shallowRef<UseLiveQueryReturn<HarmonyPackageBoardColumn> | undefined>(undefined)
  const boardItemsQuery = shallowRef<UseLiveQueryReturn<KanbanCard> | undefined>(undefined)
  const harmonyItemsQuery = shallowRef<UseLiveQueryReturn<HarmonyItem> | undefined>(undefined)

  const boards = computed<HarmonyPackageBoard[]>(() => boardQuery.value?.data.value ?? [])
  const board = computed<HarmonyPackageBoard | undefined>(() => boards.value[0])

  const columns = computed<HarmonyPackageBoardColumn[]>(() => columnsQuery.value?.data.value ?? [])
  const harmonyPackageBoardItems = computed(() => boardItemsQuery.value?.data.value ?? [])
  const harmonyItems = computed<HarmonyItem[]>(() => harmonyItemsQuery.value?.data.value ?? [])

  const isBoardLoading = computed(() => boardQuery.value?.isLoading.value ?? true)
  const isColumnsLoading = computed(() => columnsQuery.value?.isLoading.value ?? true)
  const isBoardItemsLoading = computed(() => boardItemsQuery.value?.isLoading.value ?? true)
  const isHarmonyItemsLoading = computed(() => harmonyItemsQuery.value?.isLoading.value ?? true)

  const isBoardReady = computed(() => boardQuery.value?.isReady.value ?? false)
  const isColumnsReady = computed(() => columnsQuery.value?.isReady.value ?? false)
  const isBoardItemsReady = computed(() => boardItemsQuery.value?.isReady.value ?? false)
  const isHarmonyItemsReady = computed(() => harmonyItemsQuery.value?.isReady.value ?? false)

  const hasBoardError = computed(() => boardQuery.value?.isError.value ?? false)
  const hasColumnsError = computed(() => columnsQuery.value?.isError.value ?? false)
  const hasBoardItemsError = computed(() => boardItemsQuery.value?.isError.value ?? false)
  const hasHarmonyItemsError = computed(() => harmonyItemsQuery.value?.isError.value ?? false)

  const isLoading = computed(() => {
    return isBoardLoading.value || isColumnsLoading.value || isBoardItemsLoading.value || isHarmonyItemsLoading.value
  })

  const isReady = computed(() => isBoardReady.value && isColumnsReady.value)
  const hasError = computed(
    () => hasBoardError.value || hasColumnsError.value || hasBoardItemsError.value || hasHarmonyItemsError.value,
  )

  let hasInitialized = false

  function invokeLifecycleMethods(target: unknown, methods: string[], logger?: KanbanBoardContextLoggerLike) {
    if (!target) return

    methods.forEach((method) => {
      const fn = (target as Record<string, unknown>)[method]
      if (typeof fn !== 'function') return
      try {
        ;(fn as () => void).call(target)
      } catch (error) {
        logger?.debug('Failed to invoke lifecycle method on target', {
          method,
          error,
        })
      }
    })
  }

  function disposeResources(logger?: KanbanBoardContextLoggerLike) {
    const registry = useCollectionRegistry()
    const evictCollection = (target: unknown) => {
      const id = typeof (target as any)?.id === 'string' ? ((target as any).id as string) : ''
      if (!id) return
      const evicted = registry.evict(id)
      if (evicted) {
        logger?.debug('Evicted collection registry entry', { id })
      }
    }

    invokeLifecycleMethods(boardQuery.value, ['stop', 'dispose', 'unsubscribe'], logger)
    invokeLifecycleMethods(columnsQuery.value, ['stop', 'dispose', 'unsubscribe'], logger)
    invokeLifecycleMethods(boardItemsQuery.value, ['stop', 'dispose', 'unsubscribe'], logger)
    invokeLifecycleMethods(harmonyItemsQuery.value, ['stop', 'dispose', 'unsubscribe'], logger)
    evictCollection(boardsCollection.value)
    evictCollection(columnsCollection.value)
    evictCollection(boardItemsCollection.value)
    evictCollection(packageItemsCollection.value)
    evictCollection(workitemsCollection.value)
  }

  function clearBoardScopedState() {
    boardQuery.value = undefined
    columnsQuery.value = undefined
    boardItemsQuery.value = undefined
    harmonyItemsQuery.value = undefined
    boardsCollection.value = undefined
    columnsCollection.value = undefined
    boardItemsCollection.value = undefined
    packageItemsCollection.value = undefined
    workitemsCollection.value = undefined
  }

  function reset({ logger }: { logger?: KanbanBoardContextLoggerLike } = {}) {
    disposeResources(logger)
    clearBoardScopedState()
  }

  function init({ logger, teamId, workspaceId }: InitKanbanBoardContextQueriesParams) {
    if (hasInitialized) return

    hasInitialized = true

    useKanbanBoardContextOnPlanIdChanged({ hooks, logger })
    useKanbanBoardContextOnBoardIdChanged({ hooks, logger })

    useKanbanBoardContextOnContextChanged({
      hooks,
      logger,
      boardQuery,
      columnsQuery,
      boardItemsQuery,
      harmonyItemsQuery,
      boardsCollection,
      columnsCollection,
      boardItemsCollection,
      packageItemsCollection,
      workitemsCollection,
      disposeResources: () => disposeResources(logger),
      clearBoardScopedState,
      factories: {
        createBoardsCollection: (harmonyPlanId) => getBoardsCollection(harmonyPlanId),
        createColumnsCollection: (boardId) => getColumnsCollection(boardId),
        createBoardItemsCollection: (boardId) => getBoardItemsCollection(boardId),
        createPackageItemsCollection: (harmonyPlanId) => getHarmonyItemsCollection(harmonyPlanId),
        createWorkitemsCollection: (teamId) => getWorkitemsCollection(teamId),
        createBoardQuery: (boardId, harmonyPlanId) => useBoardById(boardId, harmonyPlanId),
        createColumnsQuery: (boardId) => useColumnsByBoardId(boardId),
        createBoardItemsQuery: (boardId, workspaceId, harmonyPlanId, teamId) =>
          useBoardWorkitemsByBoardId(boardId, workspaceId, harmonyPlanId, teamId),
        createHarmonyItemsQuery: (harmonyPlanId, teamId) => useHarmonyItems(harmonyPlanId, teamId),
      },
    })

    watch(
      [harmonyPlanId, boardId, teamId, workspaceId],
      (
        [nextPlanId, nextBoardId, nextTeamId, nextWorkspaceId],
        [previousPlanId, previousBoardId, previousTeamId, previousWorkspaceId],
      ) => {
        void emitContextChanged({
          harmonyPlanId: nextPlanId,
          previousHarmonyPlanId: previousPlanId,
          boardId: nextBoardId,
          previousBoardId,
          teamId: nextTeamId,
          previousTeamId,
          workspaceId: nextWorkspaceId,
          previousWorkspaceId,
        })
      },
      { immediate: true },
    )
  }

  function onPlanIdChanged(handler: KanbanBoardContextHookMap['PlanIdChanged']) {
    return hooks.hook('PlanIdChanged', handler)
  }

  function onBoardIdChanged(handler: KanbanBoardContextHookMap['BoardIdChanged']) {
    return hooks.hook('BoardIdChanged', handler)
  }

  function onContextChanged(handler: KanbanBoardContextHookMap['ContextChanged']) {
    return hooks.hook('ContextChanged', handler)
  }

  async function emitPlanIdChanged(event: KanbanBoardContextEvents['PlanIdChanged']) {
    return hooks.callHook('PlanIdChanged', event)
  }

  async function emitBoardIdChanged(event: KanbanBoardContextEvents['BoardIdChanged']) {
    return hooks.callHook('BoardIdChanged', event)
  }

  async function emitContextChanged(event: KanbanBoardContextEvents['ContextChanged']) {
    return hooks.callHook('ContextChanged', event)
  }

  return {
    init,
    reset,
    hooks,
    onPlanIdChanged,
    onBoardIdChanged,
    onContextChanged,
    emitPlanIdChanged,
    emitBoardIdChanged,
    emitContextChanged,
    harmonyPlanId,
    boardId,
    boardsCollection,
    columnsCollection,
    boardItemsCollection,
    packageItemsCollection,
    workitemsCollection,
    boardQuery,
    columnsQuery,
    boardItemsQuery,
    harmonyItemsQuery,
    board,
    boards,
    columns,
    harmonyPackageBoardItems,
    harmonyItems,
    isLoading,
    isReady,
    hasError,
    isBoardLoading,
    isColumnsLoading,
    isBoardItemsLoading,
    isHarmonyItemsLoading,
    isBoardReady,
    isColumnsReady,
    isBoardItemsReady,
    isHarmonyItemsReady,
    hasBoardError,
    hasColumnsError,
    hasBoardItemsError,
    hasHarmonyItemsError,
  }
}
